import { readSseStream } from '~/utils/agent-chat-sse';
import {
  parseChatRunEvent,
  type ChatIntent,
  type ChatPageContext,
  type ChatRating,
  type ChatRatingReason,
  type ChatRunEvent,
} from '~/utils/agent-chat';

/**
 * The calls of the chat assistant "Suge". The chat token comes from pos-backend with the admin
 * token. The browser then calls agent-api directly with the chat token, and never sends its admin
 * token there. pos-agent `docs/chat-assistant-v1.md` owns the contract.
 */

/** An error of pos-backend or agent-api. A network error has the status 0 and the code "network". */
export class AgentChatError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.name = 'AgentChatError';
    this.status = status;
    this.code = code;
  }
}

// ── The chat token ───────────────────────────────────────────────────────────

export interface ChatToken {
  token: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export interface ChatTokenProvider {
  get(): Promise<string>;
  /** agent-api refused the token, so the next get() asks for a new one. */
  invalidate(): void;
}

interface ChatTokenResponse {
  data: { token: string; expires_at: number };
}

export async function fetchChatToken(locale: 'ja' | 'en'): Promise<ChatToken> {
  const { result, error } = await useMyFetch<ChatTokenResponse>('/api/v1/admin/agent/chat-token', {
    method: 'POST',
    body: { locale },
    skipDefaultErrorHandling: true,
  });
  if (error || !result?.data?.token) {
    const message = error?.data?.message;
    throw new AgentChatError(error?.statusCode ?? 0, typeof message === 'string' ? message : 'chat_token_failed');
  }
  return { token: result.data.token, expiresAt: result.data.expires_at };
}

/**
 * Caches the token and renews it `renewBeforeMs` before it expires. The margin covers the clock
 * difference between the browser and agent-api, and a slow request.
 */
export function createChatTokenProvider(
  fetchToken: () => Promise<ChatToken>,
  options: { now?: () => number; renewBeforeMs?: number } = {},
): ChatTokenProvider {
  const now = options.now ?? Date.now;
  const renewBeforeMs = options.renewBeforeMs ?? 60_000;
  let cached: ChatToken | null = null;
  let pending: Promise<ChatToken> | null = null;
  // invalidate() starts a new generation. A request of an older generation, for example one with
  // the old locale, still answers its own caller, but the cache does not keep its token.
  let generation = 0;

  return {
    async get() {
      if (cached && cached.expiresAt - now() > renewBeforeMs) return cached.token;
      const requestGeneration = generation;
      // Calls at the same time share one request.
      const request = (pending ??= fetchToken().finally(() => {
        if (pending === request) pending = null;
      }));
      const token = await request;
      if (requestGeneration === generation) cached = token;
      return token.token;
    },
    invalidate() {
      generation++;
      cached = null;
      pending = null;
    },
  };
}

// ── agent-api ────────────────────────────────────────────────────────────────

export interface ChatSession {
  id: string;
  agent_id: string;
  created_at: string;
}

export interface ChatSessionSummary extends ChatSession {
  /** The first question, cut by agent-api. Null before the first question. */
  title: string | null;
  ticket_uid: string | null;
  last_message_at: string;
}

/** A question has no id. An answer has the id of its message, for the feedback endpoint. */
export interface ChatSessionMessage {
  id: string | null;
  run_id: string | null;
  role: 'user' | 'assistant';
  text: string;
  created_at: string;
}

export interface ChatSessionDetail extends ChatSession {
  ticket_uid: string | null;
  messages: ChatSessionMessage[];
}

/** The keyset of the next history page: the time and the id of the last session of a page. */
export interface ChatHistoryCursor {
  before: string;
  before_id: string;
}

export type { ChatRating, ChatRatingReason } from '~/utils/agent-chat';

export interface ChatRunBody {
  message?: { text: string };
  /** agent-api uses `answer` when the body has no intent. */
  intent?: ChatIntent;
  context?: ChatPageContext;
}

interface RunView {
  id: string;
  status: string;
  result?: { text?: string | null };
}

export interface AgentChatClient {
  createSession(): Promise<ChatSession>;
  /** Starts a run and reads its event stream to the end. A chat token must stream. */
  streamRun(
    sessionId: string,
    body: ChatRunBody,
    onEvent: (event: ChatRunEvent) => void,
    signal?: AbortSignal,
  ): Promise<void>;
  /** Reads the stored events after `afterSeq`, then the new events until the run ends. */
  replayRun(
    runId: string,
    afterSeq: number | null,
    onEvent: (event: ChatRunEvent) => void,
    signal?: AbortSignal,
  ): Promise<void>;
  /** The final answer after the output filter, or null for a run without an answer. */
  getRunText(runId: string): Promise<string | null>;
  cancelRun(runId: string): Promise<void>;
  /** The chat sessions of the user, newest first. */
  listSessions(cursor?: ChatHistoryCursor): Promise<ChatSessionSummary[]>;
  getSession(sessionId: string): Promise<ChatSessionDetail>;
  /** The id is `message_id` of `run.completed`. A new rating replaces the old one. */
  sendFeedback(messageId: string, rating: ChatRating, reason?: ChatRatingReason): Promise<void>;
  /** Records the ticket that the user created from the session. agent-api only shows it as a badge. */
  recordTicket(sessionId: string, ticketUid: string): Promise<void>;
}

/** The `limit` of a history page. */
export const CHAT_HISTORY_PAGE_SIZE = 20;

export function createAgentChatClient(options: {
  baseUrl: string;
  tokens: ChatTokenProvider;
  fetch?: typeof globalThis.fetch;
}): AgentChatClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  const errorOf = async (response: Response): Promise<AgentChatError> => {
    let code = 'http_error';
    let message: string | undefined;
    try {
      const body = (await response.json()) as { error?: { code?: unknown; message?: unknown } };
      if (typeof body.error?.code === 'string') code = body.error.code;
      if (typeof body.error?.message === 'string') message = body.error.message;
    } catch {
      // The body is not the error format of agent-api, for example a page of the load balancer.
    }
    return new AgentChatError(response.status, code, message);
  };

  // The token goes in the Authorization header only, never in the URL. A 401 means that the token
  // expired or that the secret changed, so the request repeats once with a new token.
  const request = async (path: string, init: RequestInit = {}, retry = true): Promise<Response> => {
    const token = await options.tokens.get();
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    if (init.body !== undefined) headers.set('Content-Type', 'application/json');
    let response: Response;
    try {
      response = await doFetch(`${baseUrl}${path}`, { ...init, headers });
    } catch (error) {
      if (init.signal?.aborted) throw error;
      throw new AgentChatError(0, 'network');
    }
    if (response.status === 401 && retry) {
      options.tokens.invalidate();
      return request(path, init, false);
    }
    if (!response.ok) throw await errorOf(response);
    return response;
  };

  const readEvents = async (response: Response, onEvent: (event: ChatRunEvent) => void) => {
    if (!response.body) throw new AgentChatError(response.status, 'no_stream');
    try {
      await readSseStream(response.body, message => {
        const event = parseChatRunEvent(message);
        if (event) onEvent(event);
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new AgentChatError(0, 'network');
    }
  };

  return {
    async createSession() {
      const response = await request('/v1/sessions', { method: 'POST', body: JSON.stringify({ agent_id: 'chat' }) });
      return (await response.json()) as ChatSession;
    },

    async streamRun(sessionId, body, onEvent, signal) {
      const response = await request(`/v1/sessions/${encodeURIComponent(sessionId)}/runs`, {
        method: 'POST',
        headers: { Accept: 'text/event-stream' },
        body: JSON.stringify({ ...body, stream: true }),
        signal,
      });
      await readEvents(response, onEvent);
    },

    async replayRun(runId, afterSeq, onEvent, signal) {
      const headers: Record<string, string> = { Accept: 'text/event-stream' };
      if (afterSeq !== null) headers['Last-Event-ID'] = String(afterSeq);
      const response = await request(`/v1/runs/${encodeURIComponent(runId)}/events`, { headers, signal });
      await readEvents(response, onEvent);
    },

    async getRunText(runId) {
      const response = await request(`/v1/runs/${encodeURIComponent(runId)}`);
      const run = (await response.json()) as RunView;
      return typeof run.result?.text === 'string' ? run.result.text : null;
    },

    async listSessions(cursor) {
      const query = new URLSearchParams({ agent_id: 'chat', limit: String(CHAT_HISTORY_PAGE_SIZE) });
      if (cursor) {
        query.set('before', cursor.before);
        query.set('before_id', cursor.before_id);
      }
      const response = await request(`/v1/sessions?${query.toString()}`);
      return ((await response.json()) as { sessions: ChatSessionSummary[] }).sessions;
    },

    async getSession(sessionId) {
      const response = await request(`/v1/sessions/${encodeURIComponent(sessionId)}`);
      return (await response.json()) as ChatSessionDetail;
    },

    async sendFeedback(messageId, rating, reason) {
      await request(`/v1/messages/${encodeURIComponent(messageId)}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ rating, ...(reason && { reason }) }),
      });
    },

    async recordTicket(sessionId, ticketUid) {
      await request(`/v1/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ ticket_uid: ticketUid }),
      });
    },

    async cancelRun(runId) {
      try {
        await request(`/v1/runs/${encodeURIComponent(runId)}/cancel`, { method: 'POST' });
      } catch (error) {
        // The run ended before the cancel. The stop button then has nothing to stop.
        if (error instanceof AgentChatError && error.status === 409 && error.code === 'run_ended') return;
        throw error;
      }
    },
  };
}
